/** The thread composer's saved, unsent reply: one per roster entry, its text and (optionally) its formatting. */
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/common';
import type { OutreachRate } from '@discovr/types';
import { rethrowWithCode, rosterOutreachPath } from './outreachApi';

export type ComposerDraftKind = 'reply' | 'confirm' | 'counter';

export interface OutreachComposerDraftInput {
  kind: ComposerDraftKind;
  subject?: string;
  body: string;
  /** The editor's HTML for `body`; the server sanitises it. Absent on drafts saved before formatting was kept. */
  bodyHtml?: string;
  rates: OutreachRate[];
  suggestionJobId?: string;
}

export interface OutreachComposerDraft extends OutreachComposerDraftInput {
  savedBy: string;
  savedAt: string;
}

interface WireComposerDraft {
  kind: ComposerDraftKind;
  subject: string | null;
  body: string;
  body_html: string | null;
  rates: OutreachRate[] | null;
  suggestion_job_id: string | null;
  saved_by: string;
  saved_at: string;
}

const path = (entryId: string) => rosterOutreachPath(entryId, '/composer-draft');

const toDraft = (w: WireComposerDraft): OutreachComposerDraft => ({
  kind: w.kind,
  subject: w.subject ?? undefined,
  body: w.body,
  bodyHtml: w.body_html ?? undefined,
  rates: (w.rates ?? []).map(({ deliverable, amount, currency }) => ({ deliverable, amount, currency })),
  suggestionJobId: w.suggestion_job_id ?? undefined,
  savedBy: w.saved_by,
  savedAt: w.saved_at,
});

export function fetchComposerDraft(entryId: string): Promise<OutreachComposerDraft | null> {
  return apiClient
    .get<DataResponse<WireComposerDraft | null>>(path(entryId))
    .catch(rethrowWithCode)
    .then((res) => (res.data.data ? toDraft(res.data.data) : null));
}

export function saveComposerDraft(entryId: string, input: OutreachComposerDraftInput): Promise<OutreachComposerDraft> {
  return apiClient
    .put<DataResponse<WireComposerDraft>>(path(entryId), {
      kind: input.kind,
      subject: input.subject,
      body: input.body,
      body_html: input.bodyHtml,
      rates: input.rates,
      suggestion_job_id: input.suggestionJobId,
    })
    .catch(rethrowWithCode)
    .then((res) => toDraft(res.data.data));
}

export function deleteComposerDraft(entryId: string): Promise<void> {
  return apiClient
    .delete(path(entryId))
    .catch(rethrowWithCode)
    .then(() => undefined);
}
